import { AbsenceType, QueryFactory, ResourceType } from '@lmt/shared-types';

import {
  CustomDictionaryName,
  useDictionaryByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import {
  getDefaultAbsenceData,
  useAbsenceResourceSettings,
} from '@lmt/unicorn-shared/app/components/absences/common/settings';
import {
  AbsenceCreatingFormData,
  holidayPseudoResourceType,
} from '@lmt/unicorn-shared/app/components/absences/common/types';
import { SchedulingAssistant } from '@lmt/unicorn-shared/app/components/calendar/assistant/SchedulingAssistant';
import { convertIntervalToFormField } from '@lmt/unicorn-shared/app/components/calendar/visit/common/time-operations';
import {
  ResourceGroups,
  ResourceToPick,
  SelectedResources,
} from '@lmt/unicorn-shared/app/components/calendar/visit/common/types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { ComboBoxInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/ComboBoxInput';
import {
  DateTimeIntervalPicker,
  stringifyDateTimeIntervalForMemoization,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DateTimeIntervalPicker';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { getDayTimeInMillis } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import {
  convertToErrorString,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import {
  addDays,
  addMilliseconds,
  Interval,
  isValid,
  startOfDay,
} from 'date-fns';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styles from './CreateAbsence.module.scss';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from '@mui/material';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { isEmpty } from '../../measurements/copy-datablock-field/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { absenceYupSchema } from '../common/validation';
import {
  emptyArray,
  emptyObject,
} from '@lmt/unicorn-shared/app/helpers/common';

interface CreateAbsenceProps {
  selectedInterval?: Interval;
  absenceId?: number;
}

interface AbsenceReason {
  id: number;
  value: string;
  isDefault?: boolean | null;
}

const customAbsenceReasonId = -1;
const customAbsenceFieldName = 'absence_reason';

const nameof = nameofFactory<AbsenceCreatingFormData>();

export const CreateAbsence: FC<FormComponentProps & CreateAbsenceProps> = (
  props,
) => {
  const { absenceId, formType, formId, tryCompleteForm, loadBlocking } = props;

  const {
    register,
    formState,
    watch,
    setError,
    handleSubmit,
    control,
    setValue,
  } = useForm<AbsenceCreatingFormData>({
    resolver: yupResolver(absenceYupSchema) as any,
    shouldUnregister: false,
    defaultValues: getDefaultAbsenceData(props),
  });
  const modals = useModal();
  const i18next = useScopedTranslation('absences');
  const i18nextDict = useTranslation(NS_DataBlockFields);

  const [absenceType, dateTimeInterval, absenceReasonId] = watch([
    'absenceType',
    'dateTimeInterval',
    'absenceReasonId',
  ]);

  const resourceSettings = useAbsenceResourceSettings();

  const resourceQuery = QueryFactory.ResourceQuery.useGetResourcesQuery({
    includeDeleted: !!absenceId, // if we edit absence, we show all absence resources including deleted
  });
  const resources = resourceQuery.data?.data ?? [];

  const absenceReasonDictionary = useDictionaryByTitle(
    CustomDictionaryName.AbsenceReasons,
  );

  const absenceReasonsQuery =
    QueryFactory.DataBlockDictionaryItemQuery.useGetQuery(
      {
        dictionaryId: absenceReasonDictionary?.id,
        all: true,
      },
      {
        enabled: !!absenceReasonDictionary?.id,
      },
    );

  const [customAbsenceReason, setCustomAbsenceReason] =
    useState<AbsenceReason | null>(null);

  const absenceReasons: AbsenceReason[] = useMemo(() => {
    const items = (absenceReasonsQuery.data?.data ?? [])
      .filter((x) => !x.isDeleted || x.id === absenceReasonId)
      .sort((x) => (x.isDefault ? -1 : 1));
    if (customAbsenceReason) {
      return [...items, customAbsenceReason];
    }
    return items;
  }, [absenceReasonsQuery.data, customAbsenceReason, absenceReasonId]);

  const showConflictDialog = useEventCallback(() =>
    modals.showConfirm({
      title: i18next.t('already_taken_title'),
      text: i18next.t('already_taken_text'),
      okButtonText: i18next.t('save_anyway'),
    }),
  );

  const createOrUpdateAbsence = useEventCallback(
    async (data: any, absenceId?: number) => {
      if (absenceId) {
        await QueryFactory.AppointmentEventsClient.patchAbsence(
          absenceId,
          data,
        );
      } else {
        await QueryFactory.AppointmentEventsClient.createAbsence(data);
      }
    },
  );

  const submitForm = useErrorHandler<AbsenceCreatingFormData>(
    useEventCallback(async (data) => {
      const resourceIds = Object.keys(data.resources)
        .flatMap((type) => data.resources[type])
        .map((resource) => resource.id);
      if (
        !resourceIds?.length &&
        data.absenceType === AbsenceType.ResourceUnavailability
      ) {
        await modals.showAlert({
          title: i18next.t('form.resources_warning_title'),
          text: i18next.t('form.resources_warning_text'),
        });
        return;
      }

      const start = addMilliseconds(
        data.dateTimeInterval!.startDate!,
        data.dateTimeInterval!.startTimeInMills!,
      );
      const end = addMilliseconds(
        data.dateTimeInterval!.endDate!,
        data.dateTimeInterval!.endTimeInMills!,
      );

      const customValues: Record<string, string> = {};
      if (
        data.absenceReasonId === customAbsenceReasonId &&
        customAbsenceReason
      ) {
        customValues[customAbsenceFieldName] = customAbsenceReason.value;
      }

      let dto = {
        ...data,
        title: data.title ?? '',
        start,
        end,
        resourceIds,
        absenceReasonId:
          data.absenceReasonId !== customAbsenceReasonId
            ? data.absenceReasonId
            : null,
        values: customValues,
      };

      await tryCompleteForm(formType, formId, async () => {
        try {
          await createOrUpdateAbsence(dto, absenceId);
        } catch (e: any) {
          if (e.status === 400) {
            await modals.showAlert({
              title: i18next.t('event_already_exists_title'),
              text: i18next.t('event_already_exists_text'),
            });
          }
          if (e.status === 409) {
            const acceptConflict = await showConflictDialog();
            if (acceptConflict) {
              dto.acceptConflict = true;
              await createOrUpdateAbsence(dto, absenceId);
            } else {
              return;
            }
          } else {
            throw e;
          }
        }
      });
    }),
    setError,
  );

  const resourceGroups: ResourceGroups = useMemo(() => {
    const groupedResources: ResourceGroups = {};
    resources.forEach((resource) => {
      let resourceList: ResourceToPick[] =
        groupedResources[ResourceType[resource.type]];
      if (!resourceList) {
        resourceList = [];
        groupedResources[ResourceType[resource.type]] = resourceList;
      }
      resourceList.push({
        id: resource.id,
        name: resource.name,
        isDeleted: resource.isDeleted,
        order: resource.manualOrderPosition,
        getTimeSlotsFromAppointments: (appointments) =>
          appointments
            .filter(
              (appointment) =>
                appointment.resourceIds &&
                appointment.resourceIds.indexOf(resource.id) >= 0,
            )
            .map((appointment) => ({
              start: appointment.start,
              end: appointment.end,
            })),
      });
    });
    return groupedResources;
  }, [resources]);

  const resourceGroupsForScheduler: ResourceGroups = useMemo(() => {
    if (absenceType === AbsenceType.ResourceUnavailability) {
      return resourceGroups;
    }
    const groupedResources: ResourceGroups = {};
    groupedResources[holidayPseudoResourceType] = [];
    return groupedResources;
  }, [resourceGroups, absenceType, dateTimeInterval]);

  const [selectedResources, setSelectedResources] = useState<SelectedResources>(
    {},
  );

  const setResourceSelected = useEventCallback(
    (
      type: string,
      resourceId: number | null,
      selected: boolean,
      resource?: ResourceToPick,
    ) => {
      setSelectedResources((actualSelectedResources) => {
        const newSelectedResources = { ...actualSelectedResources };
        if (selected) {
          const foundResource =
            resource ||
            resourceGroups[type]?.find(
              (resource) => resource.id === resourceId,
            );
          if (foundResource) {
            const selectedResourcesOfType = newSelectedResources[type] || [];
            newSelectedResources[type] = [
              ...selectedResourcesOfType,
              foundResource,
            ];
          }
        } else if (
          !resourceId ||
          newSelectedResources[type]?.find(
            (resource) => resource.id === resourceId,
          )
        ) {
          if (!resourceId) {
            delete newSelectedResources[type];
          } else {
            newSelectedResources[type] = newSelectedResources[type].filter(
              (resource) => resource.id !== resourceId,
            );
          }
        }
        return newSelectedResources;
      });
    },
  );

  useEffect(function loadAbsenceValues() {
    if (!absenceId) {
      return;
    }
    const loadAbsence = async () => {
      const absence = await QueryFactory.AppointmentEventsClient.getAbsence(
        absenceId!,
      );
      setValue(nameof('absenceType'), absence.type);
      setValue(nameof('title'), absence.title);
      setValue(nameof('absenceReasonId'), absence.absenceReasonId);
      setValue(nameof('dateTimeInterval'), {
        startDate: startOfDay(absence.start),
        startTimeInMills: getDayTimeInMillis(absence.start),
        endDate: startOfDay(absence.end),
        endTimeInMills: getDayTimeInMillis(absence.end),
      });
      if (absence.resourceIds?.length) {
        const preselectedResources: SelectedResources = {};
        Object.keys(resourceGroups).map((key) => {
          preselectedResources[key] = resourceGroups[key].filter((item) =>
            absence.resourceIds?.includes(item.id),
          );
        });
        if (!isEmpty(preselectedResources)) {
          setSelectedResources(preselectedResources);
        }
      }
      if (absence.customFields?.[customAbsenceFieldName]) {
        setCustomAbsenceReason({
          id: customAbsenceReasonId,
          value: absence.customFields[customAbsenceFieldName],
        });
        setValue('absenceReasonId', customAbsenceReasonId, validateAndDirty);
      }
    };
    loadAbsence().catch((e) => console.error('loadAbsence', e));
  }, []);

  useEffect(() => {
    setValue('resources', selectedResources, validateAndDirty);
  }, [selectedResources]);

  const onIntervalChanged = useEventCallback((newInterval: Interval) => {
    setValue(
      'dateTimeInterval',
      convertIntervalToFormField(newInterval),
      validateAndDirty,
    );
  });

  const startDateTime = useMemo(
    () =>
      dateTimeInterval.startDate &&
      dateTimeInterval.startTimeInMills !== undefined &&
      dateTimeInterval.startTimeInMills !== null &&
      isValid(dateTimeInterval.startDate) &&
      isValid(dateTimeInterval.startTimeInMills)
        ? addMilliseconds(
            dateTimeInterval.startDate,
            dateTimeInterval.startTimeInMills,
          )
        : undefined,
    [stringifyDateTimeIntervalForMemoization(dateTimeInterval)],
  );

  const endDateTime = useMemo(
    () =>
      dateTimeInterval.endDate &&
      dateTimeInterval.endTimeInMills !== undefined &&
      dateTimeInterval.endTimeInMills !== null &&
      isValid(dateTimeInterval.endDate) &&
      isValid(dateTimeInterval.endTimeInMills)
        ? addMilliseconds(
            dateTimeInterval.endDate,
            dateTimeInterval.endTimeInMills,
          )
        : undefined,
    [stringifyDateTimeIntervalForMemoization(dateTimeInterval)],
  );
  const selectedInterval: Interval | undefined = useMemo(() => {
    return (
      startDateTime &&
      endDateTime && {
        start: startDateTime,
        end: endDateTime,
      }
    );
  }, [startDateTime?.getTime(), endDateTime?.getTime()]);

  /**
   * Holidays take the whole day, so we need to adjust interval on type switching
   */
  useEffect(
    function resetValuesOnAbsenceTypeChange() {
      // we cannot switch absence type when edit existing absence
      if (absenceId) {
        return;
      }
      if (!startDateTime || !endDateTime) return;
      if (absenceType === AbsenceType.Holiday) {
        const start = startOfDay(startDateTime);
        const end =
          endDateTime.getTime() === startOfDay(endDateTime).getTime()
            ? endDateTime
            : startOfDay(addDays(endDateTime, 1));
        setValue(
          'dateTimeInterval',
          convertIntervalToFormField({ start, end }),
          validateAndDirty,
        );
        setSelectedResources({});
      }
    },
    [absenceType],
  );

  const deleteAbsence = useEventCallback(async () => {
    if (!absenceId) {
      return;
    }
    const isConfirmed = await modals.showDeletionConfirmation({
      title: i18next.t('form.confirm_delete_title'),
      text: i18next.t('form.confirm_delete_text'),
      okButtonText: i18next.t('form.confirm_delete_button'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18next.t('form.loading_delete'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.AppointmentEventsClient.deleteAppointment(
              absenceId,
            );
          });
        } catch (err) {
          await modals.showError({ text: convertToErrorString(err) });
        }
      }).catch((e) => console.error('deleteAbsence', e));
    }
  });

  const selectedAbsenceReason = useMemo(() => {
    if (!absenceReasonId) {
      return undefined;
    }
    return absenceReasons.find((x) => x.id === absenceReasonId);
  }, [absenceReasonId, absenceReasons]);

  return (
    <form
      className={styles.absenceForm}
      onSubmit={handleSubmit(submitForm.handler)}
    >
      <div className="globalFormContentRoot">
        <div className={'globalFormContentBody'}>
          <div className={styles.section}>
            <Field title={i18next.t('form.field_type')}>
              <div
                className={styles.radioButtonsContainer}
                data-test-id="absence-type"
              >
                <RadioButton
                  name={'holiday-radio-button'}
                  title={i18next.t('holiday')}
                  checked={absenceType === AbsenceType.Holiday}
                  onChange={() => setValue('absenceType', AbsenceType.Holiday)}
                  disabled={!!absenceId}
                />
                <RadioButton
                  name={'resource-unavailability-radio-button'}
                  className={styles.radioButtonRight}
                  title={i18next.t('resource_unavailability')}
                  checked={absenceType === AbsenceType.ResourceUnavailability}
                  onChange={() =>
                    setValue('absenceType', AbsenceType.ResourceUnavailability)
                  }
                  disabled={!!absenceId}
                />
              </div>
            </Field>
            {absenceType === AbsenceType.Holiday && (
              <Field
                title={i18next.t('form.field_name')}
                required={true}
                className={styles.nameField}
              >
                <Input
                  {...register('title', {
                    ...requiredRule(),
                  })}
                  errorText={formState.errors.title?.message}
                />
              </Field>
            )}
            {absenceType === AbsenceType.ResourceUnavailability && (
              <Field
                title={i18next.t('form.field_reason')}
                className={styles.nameField}
              >
                <ComboBoxInput
                  required={false}
                  options={absenceReasons}
                  {...register('absenceReasonId')}
                  onValueChanged={async (newValue, existingItem) => {
                    if (existingItem) {
                      setValue(
                        'absenceReasonId',
                        existingItem.id,
                        validateAndDirty,
                      );
                    } else {
                      const absenceReasonName = newValue?.trim();
                      if (absenceReasonName) {
                        setCustomAbsenceReason({
                          id: customAbsenceReasonId,
                          value: absenceReasonName,
                        });
                        setValue(
                          'absenceReasonId',
                          customAbsenceReasonId,
                          validateAndDirty,
                        );
                      } else {
                        setValue('absenceReasonId', null, validateAndDirty);
                        setCustomAbsenceReason(null);
                      }
                    }
                  }}
                  labelFunction={(item) => {
                    return item.isDefault
                      ? i18nextDict.t(item.value as any)
                      : item.value;
                  }}
                  value={selectedAbsenceReason}
                  errorText={formState.errors.absenceReasonId?.message}
                  emptyLabel={i18next.t('form.field_reason_placeholder')}
                />
              </Field>
            )}
          </div>
          <div className="globalFormsContentSeparator" />
          <div className={styles.section}>
            <Controller
              control={control}
              name={'dateTimeInterval'}
              render={({ field: { onChange, value } }) => (
                <DateTimeIntervalPicker
                  required={true}
                  value={value}
                  onIntervalChanged={onChange}
                  isDateOnly={absenceType === AbsenceType.Holiday}
                  startDateError={
                    !!formState.errors.dateTimeInterval?.startDate
                  }
                  startDateErrorText={
                    (formState.errors.dateTimeInterval?.startDate as any)
                      ?.message
                  }
                  startTimeError={
                    !!formState.errors.dateTimeInterval?.startTimeInMills
                  }
                  startTimeErrorText={
                    formState.errors.dateTimeInterval?.startTimeInMills?.message
                  }
                  endDateError={!!formState.errors.dateTimeInterval?.endDate}
                  endDateErrorText={
                    (formState.errors.dateTimeInterval?.endDate as any)?.message
                  }
                  endTimeError={
                    !!formState.errors.dateTimeInterval?.endTimeInMills
                  }
                  endTimeErrorText={
                    formState.errors.dateTimeInterval?.endTimeInMills?.message
                  }
                />
              )}
            ></Controller>
          </div>
          <div className="globalFormsContentSeparator" />

          <div className={styles.section} data-test-id="scheduling-assistant">
            <Field
              title={i18next.t('form.resources')}
              required={absenceType === AbsenceType.ResourceUnavailability}
              className={styles.field}
              error={
                absenceType === AbsenceType.ResourceUnavailability &&
                (formState.errors.resources as any)?.message
              }
            >
              <SchedulingAssistant
                selectedInterval={selectedInterval}
                resourceSettings={resourceSettings}
                resourceGroups={resourceGroupsForScheduler}
                onIntervalChanged={onIntervalChanged}
                selectedResources={selectedResources}
                setResourceSelected={setResourceSelected}
                takenTimeSlots={emptyArray()}
                takenTimeSlotsForResources={emptyObject()}
                isWeekView={true}
                stepsInCell={
                  absenceType === AbsenceType.Holiday ? 1 : undefined
                }
                typesWithAnyAvailableResource={emptyArray()}
                unavailableTimeSlots={emptyObject()}
              />
            </Field>
          </div>
        </div>
        <div className={styles.footer}>
          <span>
            {!!submitForm.overallServerError ? (
              <span className={styles.errorText}>
                {submitForm.overallServerError}
              </span>
            ) : (
              <></>
            )}
          </span>
          <div className={styles.btnGroup}>
            {absenceId && (
              <Button
                color={ButtonColor.Danger}
                title={i18next.t('form.delete_button')}
                onClick={deleteAbsence}
                className="globalFormContentSubmitButton"
              />
            )}
            <SubmitButton
              title={i18next.t('form.submit_button_title')}
              submitting={formState.isSubmitting}
              className="globalFormContentSubmitButton"
            />
          </div>
        </div>
      </div>
    </form>
  );
};
